import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';

const AccountDropdown = ({ 
  user = {
    name: 'User',
    email: '',
    avatar: null,
    creditsRemaining: 0
  },
  onLogout,
  onPurchaseCredits
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const menuItems = [
    { icon: 'LayoutDashboard', label: 'My Dashboard', path: '/user-dashboard', description: 'Credits & account overview' },
    { icon: 'History', label: 'Analysis History', path: '/dashboard-analysis-history', description: 'View past analyses' },
    { icon: 'CreditCard', label: 'Purchase Credits', path: '/purchase-credits', description: 'Buy photo analysis credits' },
    { icon: 'Settings', label: 'Settings', path: '/settings', description: 'App preferences' }
  ];

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2) || 'U';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event?.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="account-dropdown-trigger flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
          {getInitials(user?.name)}
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} color="#6b7280" />
      </button>

      {isOpen && (
        <div className="account-dropdown-content">
          <div className="account-dropdown-header">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-bold">
                {getInitials(user?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="account-dropdown-name truncate">{user?.name}</div>
                <div className="account-dropdown-email truncate">{user?.email}</div>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
              <span className="text-xs text-purple-600 font-medium">Credits remaining</span>
              <span className="text-sm font-bold text-purple-700">{user?.creditsRemaining ?? 0}</span>
            </div>
            {(user?.creditsRemaining ?? 0) <= 2 && (
              <button
                onClick={() => { setIsOpen(false); if (onPurchaseCredits) onPurchaseCredits(); }}
                className="mt-3 w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
              >
                <Icon name="Plus" size={16} />
                Buy More Credits
              </button>
            )}
          </div>

          <div className="account-dropdown-section">
            {menuItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className="account-dropdown-item"
                onClick={() => setIsOpen(false)}
              >
                <Icon name={item?.icon} size={18} className="account-dropdown-item-icon" />
                <div className="flex-1">
                  <div className="account-dropdown-item-text">{item?.label}</div>
                  <div className="text-xs text-muted-foreground caption">{item?.description}</div>
                </div>
              </Link>
            ))}
          </div>

          <div className="account-dropdown-divider" />

          <div className="account-dropdown-section">
            <button
              className="account-dropdown-item w-full text-left"
              onClick={() => { setIsOpen(false); if (onLogout) onLogout(); }}
            >
              <Icon name="LogOut" size={18} className="account-dropdown-item-icon" />
              <div className="account-dropdown-item-text">Sign Out</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDropdown;